import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { Search, MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { fixLeafletIcons, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '../../utils/leafletIcons';
import './VenueLocationPicker.css';

fixLeafletIcons();

const NOMINATIM_HEADERS = {
  Accept: 'application/json',
  'User-Agent': 'WeddingVenueAdmin/1.0 (venue location picker)',
};

async function searchPlaces(query) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6`;
  const res = await fetch(url, { headers: NOMINATIM_HEADERS });
  if (!res.ok) throw new Error('Search failed');
  return res.json();
}

async function reverseGeocode(lat, lng) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
  const res = await fetch(url, { headers: NOMINATIM_HEADERS });
  if (!res.ok) return null;
  const data = await res.json();
  return data.display_name || null;
}

function MapRecenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom, { animate: true });
  }, [center, zoom, map]);
  return null;
}

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const VenueLocationPicker = ({
  latitude,
  longitude,
  address,
  onLocationChange,
  mapOpen: mapOpenControlled,
  onMapOpenChange,
}) => {
  const [mapOpenInternal, setMapOpenInternal] = useState(false);
  const mapOpen = mapOpenControlled !== undefined ? mapOpenControlled : mapOpenInternal;
  const setMapOpen = onMapOpenChange || setMapOpenInternal;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const hasPin = latitude !== '' && longitude !== '' && !Number.isNaN(Number(latitude)) && !Number.isNaN(Number(longitude));

  const position = useMemo(() => {
    if (!hasPin) return null;
    return { lat: parseFloat(latitude), lng: parseFloat(longitude) };
  }, [hasPin, latitude, longitude]);

  const mapCenter = position ? [position.lat, position.lng] : [DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng];
  const mapZoom = position ? 16 : DEFAULT_MAP_ZOOM;

  const applyPosition = useCallback(
    async (lat, lng, addressOverride) => {
      const latStr = lat.toFixed(6);
      const lngStr = lng.toFixed(6);
      let nextAddress = addressOverride;
      if (nextAddress === undefined) {
        setGeocoding(true);
        try {
          nextAddress = (await reverseGeocode(lat, lng)) || address;
        } finally {
          setGeocoding(false);
        }
      }
      onLocationChange({
        latitude: latStr,
        longitude: lngStr,
        address: nextAddress ?? address,
      });
    },
    [address, onLocationChange]
  );

  const openMap = () => setMapOpen(true);

  useEffect(() => {
    if (!mapOpen || searchQuery.trim().length < 3) {
      setSearchResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchPlaces(searchQuery.trim());
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [searchQuery, mapOpen]);

  const handleSearchSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setSearchQuery(result.display_name);
    setSearchResults([]);
    applyPosition(lat, lng, result.display_name);
  };

  return (
    <div className="venue-location-picker">
      <div className="venue-location-actions">
        <button type="button" className="btn-secondary venue-map-open-btn" onClick={openMap}>
          <MapPin size={18} />
          {hasPin ? 'Change pin on map' : 'Pin location on map'}
        </button>
        {hasPin && (
          <span className="venue-location-coords">
            {parseFloat(latitude).toFixed(5)}, {parseFloat(longitude).toFixed(5)}
            {geocoding && ' · updating address…'}
          </span>
        )}
      </div>

      {mapOpen && (
        <div className="venue-location-map-panel">
          <div className="venue-location-search">
            <Search size={18} className="venue-location-search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for church, hotel, city…"
              autoComplete="off"
            />
          </div>
          {searching && <p className="venue-location-hint">Searching…</p>}
          {!searching && searchResults.length > 0 && (
            <ul className="venue-location-results">
              {searchResults.map((r) => (
                <li key={`${r.place_id}-${r.lat}`}>
                  <button type="button" onClick={() => handleSearchSelect(r)}>
                    {r.display_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="venue-location-hint">Click the map to place or move the pin. Drag the marker to fine-tune.</p>
          <div className="venue-location-map-wrap">
            <MapContainer center={mapCenter} zoom={mapZoom} className="venue-location-map">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapRecenter center={mapCenter} zoom={mapZoom} />
              <MapClickHandler onPick={applyPosition} />
              {position && (
                <Marker
                  position={[position.lat, position.lng]}
                  draggable
                  eventHandlers={{
                    dragend: (e) => {
                      const { lat, lng } = e.target.getLatLng();
                      applyPosition(lat, lng);
                    },
                  }}
                />
              )}
            </MapContainer>
          </div>
          <button type="button" className="venue-location-done" onClick={() => setMapOpen(false)}>
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default VenueLocationPicker;
